import Popover from '@material-ui/core/Popover';
import React, { useEffect, useState } from 'react';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ArchiveIcon from '@material-ui/icons/Archive';
import UnarchiveIcon from '@material-ui/icons/Unarchive';
import EditIcon from '@material-ui/icons/Edit';
import ViewIcon from '@material-ui/icons/RemoveRedEye';
import { useStyles } from './styles';
import ConfirmationDialog from '../ConfirmationDialog/ConfirmationDialog';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import {
  archiveAttribute,
  unArchiveAttribute,
} from '../../redux/actions/attributesActions';
import { STATUS } from '../../types/status';
import { isEditable } from '../AttributesDialog/isEditable';

interface PopoverButtonProps {
  attribute: any;
  onEdit: () => void;
  onView: () => void;
}

type ListOption = {
  Icon: typeof ViewIcon | typeof EditIcon | typeof ArchiveIcon | typeof UnarchiveIcon;
  onClick: () => void;
  primary: string;
  disabled?: boolean;
};

const PopoverButton: React.FC<PopoverButtonProps> = ({ attribute, onEdit, onView }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  const [archiveModalIsOpen, setArchiveModalIsOpen] = useState(false);
  const isArchived = STATUS[attribute.status] === STATUS.ARCHIVED;

  const [anchorEl, setAnchorEl] = React.useState(null);

  const { updated } = useSelector(
    (state: RootStateOrAny) => state.AttributesReducer,
  );

  const handleClick = event => {
    setIsOpen(true);
    setAnchorEl(event.currentTarget);
  };

  const handleEdit = () => {
    setIsOpen(false);
    setAnchorEl(null);
    onEdit();
  };

  const handleView = () => {
    setIsOpen(false);
    setAnchorEl(null);
    onView();
  };

  const handleClose = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  const handleInitialArchive = () => {
    setIsOpen(false);
    setArchiveModalIsOpen(true);
  }

  const handleArchiveToggle = async () => {
    const { id } = attribute;

    dispatch(isArchived ? unArchiveAttribute(id) : archiveAttribute(id));
  };

  useEffect(() => {
    setArchiveModalIsOpen(false);
  }, [updated]);

  const id = isOpen ? 'simple-popover' : undefined;
  const action = isArchived ? 'Unarchive' : 'Archive';
  const dialogText = isArchived
    ? 'The attribute will become available again in its original regions, and it will be reactivated in the stores where it was previously active. Are you sure you want to proceed with unarchiving this attribute?'
    : 'Archiving this attribute will remove it from all stores where it is currently active. Are you sure you want to proceed with archiving this attribute?';

  const listOptionData = [
    { Icon: ViewIcon, onClick: handleView, primary: 'View' },
    { Icon: EditIcon, onClick: handleEdit, primary: 'Edit', disabled: !isEditable(attribute) },
    isArchived ? { Icon: UnarchiveIcon, onClick: handleInitialArchive, primary: 'Unarchive' } : undefined,
    !isArchived ? { Icon: ArchiveIcon, onClick: handleInitialArchive, primary: 'Archive' } : undefined,
  ].filter(Boolean);

  return (
    <div>
      <button className={classes.buttonNoBorder} onClick={handleClick}>
        <MoreVertIcon
          fontSize="small"
          style={{ color: 'rgba(0, 0, 0, 0.54)' }}
        />
      </button>
      <Popover
        open={isOpen}
        id={id}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <List component="nav" aria-label="mailbox folders">
        {
          (listOptionData as unknown as ListOption[]).map(({ Icon, onClick, primary, disabled }: ListOption) => (
            <ListItem button className={classes.listItem} disabled={!!disabled}>
              <div className={classes.iconItem}>
                <Icon fontSize="small" />
              </div>
              <ListItemText onClick={onClick} primary={primary} />
            </ListItem>
          ))
        }
        </List>
      </Popover>
      <ConfirmationDialog
        title={`${action} Attribute`}
        confirmText={`${action}`}
        isOpen={archiveModalIsOpen}
        onConfirmation={handleArchiveToggle}
        onClose={() => setArchiveModalIsOpen(false)}
      >
        {dialogText}
      </ConfirmationDialog>
    </div>
  );
};

export default PopoverButton;
